import React, { useState } from 'react';
import { X, Mail, MessageCircle, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ForgotPasswordModal = ({ isOpen, onClose, defaultEmail = '' }) => {
  const [email, setEmail] = useState(defaultEmail);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, {
        email: email.trim().toLowerCase(),
        message: message.trim() || undefined,
      });
      setSuccess(true);
      setSuccessMsg(res.data?.message || 'Request received. Our team will contact you shortly.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail(defaultEmail);
    setMessage('');
    setError('');
    setSuccess(false);
    setSuccessMsg('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-md p-4 animate-in fade-in">
      <Card className="w-full max-w-md bg-white border-2 border-blue-500/30 shadow-2xl shadow-blue-500/20 animate-in slide-in-from-bottom">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl text-blue-600 flex items-center gap-2">
              🔐 Forgot Password
            </CardTitle>
            <button
              onClick={handleClose}
              className="text-slate-400 hover:text-slate-600 transition-all hover:rotate-90 hover:scale-110"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          {!success && (
            <p className="text-sm text-slate-500 mt-2">
              Enter your registered email. Our team will verify your identity and contact you on WhatsApp with a new password within 24 hours.
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {success ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Request Submitted!</h3>
              <p className="text-sm text-slate-600 mb-4">{successMsg}</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-left mb-4">
                <p className="text-xs text-slate-600 mb-1 font-semibold">For faster response:</p>
                <a
                  href="https://wa.me/918965883955?text=Hi%2C%20I%20requested%20a%20password%20reset%20for%20my%20DropSquadz%20account."
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-green-700 hover:text-green-800"
                >
                  <MessageCircle className="w-4 h-4" />
                  Message us on WhatsApp
                </a>
              </div>
              <Button onClick={handleClose} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                Got it
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border-2 border-red-200 rounded-lg text-red-600 text-sm flex items-center">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="fp-email" className="text-slate-700 flex items-center font-medium">
                  <Mail className="w-4 h-4 mr-2 text-blue-500" />
                  Registered Email
                </Label>
                <Input
                  id="fp-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-500"
                  placeholder="your@email.com"
                  required
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fp-msg" className="text-slate-700 font-medium">
                  Additional Note (optional)
                </Label>
                <textarea
                  id="fp-msg"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-slate-800 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-sm min-h-[70px]"
                  placeholder="e.g. Best time to reach me, alternate phone, etc."
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/30"
              >
                {loading ? (
                  <span className="flex items-center">
                    <div className="w-4 h-4 mr-2 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Submitting...
                  </span>
                ) : (
                  'Submit Request'
                )}
              </Button>
              <p className="text-xs text-slate-400 text-center">
                For instant help, message us on{' '}
                <a
                  href="https://wa.me/918965883955"
                  target="_blank"
                  rel="noreferrer"
                  className="text-green-600 hover:underline"
                >
                  WhatsApp
                </a>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordModal;
